import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Bot } from "lucide-react";
import api from "../../services/api";
import PromptEditor from "../../components/PromptEditor";
import type { Prompt } from "../../types/admin.types";

const PromptManagementTab: React.FC = () => {
  const { t } = useTranslation(["admin", "common"]);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [promptsLoading, setPromptsLoading] = useState<boolean>(true);
  const [promptFilter, setPromptFilter] = useState<string>("all");

  useEffect(() => {
    fetchPrompts();
  }, []);

  const fetchPrompts = async (): Promise<void> => {
    setPromptsLoading(true);
    try {
      const response = (await api.prompts.getAllPrompts()) as {
        prompts?: Prompt[];
      };
      setPrompts(response.prompts || []);
    } catch (error) {
      const err = error as {
        response?: { data?: { error?: string; message?: string } };
        message: string;
      };
      console.error("Error fetching prompts:", error);
      alert(
        "Error fetching prompts: " +
          (err.response?.data?.error ||
            err.response?.data?.message ||
            err.message),
      );
    } finally {
      setPromptsLoading(false);
    }
  };

  const handlePromptUpdate = (key: string, value: string): void => {
    setPrompts((prev) =>
      prev.map((p) => (p.key === key ? { ...p, value } : p)),
    );
  };

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>
          <Bot size={16} /> {t("prompts.title")}
        </h2>
        <p>{t("prompts.description")}</p>
      </div>

      <div className="prompts-filter">
        <label htmlFor="prompt-filter-category">
          {t("prompts.filterLabel")}:
        </label>
        <select
          id="prompt-filter-category"
          value={promptFilter}
          onChange={(e) => setPromptFilter(e.target.value)}
          className="filter-select"
        >
          <option value="all">{t("prompts.filters.allCategories")}</option>
          {[...new Set(prompts.map((p) => p.category))].map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>
      </div>

      {promptsLoading ? (
        <div className="loading">{t("prompts.loading")}</div>
      ) : (
        <div className="prompts-list">
          {prompts
            .filter(
              (p) => promptFilter === "all" || p.category === promptFilter,
            )
            .map((prompt) => (
              <PromptEditor
                key={prompt.key}
                prompt={prompt}
                onUpdate={handlePromptUpdate}
              />
            ))}
          {prompts.filter(
            (p) => promptFilter === "all" || p.category === promptFilter,
          ).length === 0 && (
            <div className="no-results">{t("prompts.noResults")}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default PromptManagementTab;
